import { AlertTriangle } from "lucide-react";

/**
 * Persistent environment banner. Prevents "wrong environment" fat-finger
 * mistakes — the classic "I thought I was on preview" scenario.
 *
 * Resolution order (most authoritative wins):
 *   1. **Hostname check** at runtime — if we're on a known production
 *      domain (steelecloud.com.au), we ALWAYS hide the banner regardless
 *      of what the compiled-in ``REACT_APP_ENV_LABEL`` says. This matters
 *      because Create-React-App bakes env vars at build time, so a
 *      ``preview`` label in ``.env`` would otherwise ship straight to
 *      production and scare real customers.
 *   2. ``REACT_APP_ENV_LABEL``:
 *      - ``preview`` → bright amber strip
 *      - ``staging`` → cyan strip
 *      - anything else / unset → nothing rendered
 */

// Any hostname matching one of these is treated as production and the
// banner is force-hidden. Add new custom domains here.
const PROD_HOSTS = [
  "steelecloud.com.au",
  "www.steelecloud.com.au",
];

function isProdHost() {
  if (typeof window === "undefined") return false;
  const host = (window.location.hostname || "").toLowerCase();
  return PROD_HOSTS.includes(host);
}

export default function EnvironmentBanner() {
  // Runtime override — production domain always suppresses the banner.
  if (isProdHost()) return null;

  const label = (process.env.REACT_APP_ENV_LABEL || "").toLowerCase();
  if (!label || label === "live" || label === "production" || label === "prod") return null;

  const style = label === "preview"
    ? "bg-amber-500 text-slate-950 border-amber-400"
    : label === "staging"
    ? "bg-cyan-500 text-slate-950 border-cyan-400"
    : "bg-slate-700 text-slate-100 border-slate-600";

  const text = label === "preview"
    ? "PREVIEW — test data only. Any change here does NOT affect the live site."
    : label === "staging"
    ? "STAGING — pre-release environment."
    : `Environment: ${label.toUpperCase()}`;

  return (
    <div
      data-testid="env-banner"
      className={`sticky top-0 z-[60] w-full flex items-center justify-center gap-2 px-4 py-1 border-b ${style} text-[11px] font-semibold uppercase tracking-widest shadow-sm`}
    >
      <AlertTriangle className="w-3.5 h-3.5" />
      <span>{text}</span>
    </div>
  );
}
